import { useCallback, useEffect, useState } from 'react';
import { BookOpen, Download, Loader2, RefreshCw } from 'lucide-react';
import { api } from '../api';
import { useAvisos } from '../lib/contextoAvisos';
import Ayuda from '../ui/Ayuda';
import Motivo from '../experiencia/Motivo';
import NotaExplicativa from '../ui/NotaExplicativa';
import { guardarArchivo } from '../ui/descarga';
import { pesos } from './dinero';

/* =====================================================================
   Pólizas · ERP-OPERATIVO EF4
   =====================================================================
   TEGO asienta cada cobro y cada pago (una vez, cuadrado) y aquí se
   exporta para el contador. El catálogo de cuentas nace sembrado y el
   contador lo completa; el papel de cada cuenta (CAJA, BANCOS…) es lo
   que TEGO usa para asentar solo.
   ===================================================================== */
export default function Polizas() {
  const avisos = useAvisos();
  const [d, setD] = useState(null);
  const [catalogo, setCatalogo] = useState([]);
  const [error, setError] = useState('');
  const [generando, setGenerando] = useState(false);
  const [cuenta, setCuenta] = useState({ codigo: '', nombre: '', papel: '' });

  const [tick, setTick] = useState(0);
  const cargar = useCallback(() => setTick((t) => t + 1), []);
  useEffect(() => {
    let vivo = true;
    Promise.all([api.get('/polizas'), api.get('/polizas/catalogo')]).then(([p, c]) => {
      if (!vivo) return;
      setD(p); setCatalogo(c); setError('');
    }).catch((e) => { if (vivo) setError(e.message); });
    return () => { vivo = false; };
  }, [tick]);

  async function generar() {
    setGenerando(true);
    try { const r = await api.post('/polizas/generar', {}); avisos.exito(r.mensaje); cargar(); }
    catch (e) { avisos.error(e.message); } finally { setGenerando(false); }
  }

  async function guardarCuenta(e) {
    e.preventDefault();
    try {
      const r = await api.put('/polizas/catalogo', { ...cuenta, papel: cuenta.papel || null });
      avisos.exito(r.mensaje); setCuenta({ codigo: '', nombre: '', papel: '' }); cargar();
    } catch (err) { avisos.error(err.message); }
  }

  if (error && !d) return <p className="p-4 text-sm text-red-700">{error}</p>;
  if (!d) {
    return (
      <p className="flex items-center gap-2 p-4 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Leyendo las pólizas…
      </p>
    );
  }

  return (
    <div className="flex max-w-5xl flex-col gap-5 p-4">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">Pólizas</h1>
        <p className="text-gray-600">La huella contable de cada cobro y cada pago, para tu contador.</p>
        <Ayuda id="finanzas.polizas.pantalla" />
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={generar} disabled={generando}
                className="inline-flex items-center gap-2 rounded-xl bg-[var(--color-marca)] px-4 py-2 font-semibold text-white disabled:bg-gray-300">
          <RefreshCw className={`h-4 w-4 ${generando ? 'animate-spin' : ''}`} aria-hidden="true" />
          Asentar lo pendiente
        </button>
        {d.formatos.map((f) => (
          <button key={f.clave} type="button"
                  onClick={async () => guardarArchivo(await api.bajar(`/polizas/exportar.${f.clave}`), `polizas.${f.clave === 'generico' ? 'csv' : f.clave}`)}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--color-marca)]">
            <Download className="h-4 w-4" aria-hidden="true" /> Exportar · {f.nombre}
          </button>
        ))}
        <Ayuda id="finanzas.polizas.formato" />
      </div>

      <section className="rounded-xl border border-gray-200">
        <h2 className="border-b border-gray-200 px-3 py-2 font-semibold text-gray-800">
          Pólizas ({d.polizas.length})
        </h2>
        {d.polizas.length === 0 ? (
          <NotaExplicativa titulo="Sin pólizas">
            Pulsa «Asentar lo pendiente»: TEGO arma una póliza por cada cobro y cada pago que no tenga la suya. Lo ya asentado no se repite.
          </NotaExplicativa>
        ) : (
          <ul className="divide-y divide-gray-100 text-sm">
            {d.polizas.slice(-100).reverse().map((p) => (
              <li key={p.id} className="px-3 py-2">
                <div className="flex flex-wrap items-center gap-x-3">
                  <BookOpen className="h-4 w-4 text-gray-400" aria-hidden="true" />
                  <span className="font-mono text-gray-500">{p.folio}</span>
                  <span className="text-gray-500">{p.fecha}</span>
                  <span className={`text-xs ${p.tipo === 'INGRESO' ? 'text-green-700' : 'text-red-700'}`}>{p.tipo}</span>
                  <span className="flex-1 text-gray-900">{p.concepto}</span>
                  <span className="tabular-nums">{pesos(p.total_debe)}</span>
                </div>
                <ul className="ml-7 text-xs text-gray-600">
                  {p.renglones.map((r, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="font-mono">{r.cuenta}</span>
                      <span className="flex-1">{r.nombre}</span>
                      <span className="w-24 text-right tabular-nums">{Number(r.debe) ? pesos(r.debe) : ''}</span>
                      <span className="w-24 text-right tabular-nums">{Number(r.haber) ? pesos(r.haber) : ''}</span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-xl border border-gray-200">
        <h2 className="border-b border-gray-200 px-3 py-2 font-semibold text-gray-800">Catálogo de cuentas</h2>
        <ul className="divide-y divide-gray-100 text-sm">
          {catalogo.map((c) => (
            <li key={c.id} className="flex gap-3 px-3 py-1.5">
              <span className="font-mono text-gray-500">{c.codigo}</span>
              <span className="flex-1">{c.nombre}</span>
              {c.papel && <span className="text-xs text-gray-500">TEGO la usa como {c.papel}</span>}
            </li>
          ))}
        </ul>
        <form onSubmit={guardarCuenta} className="grid gap-2 border-t border-gray-200 p-3 sm:grid-cols-4">
          <input value={cuenta.codigo} onChange={(e) => setCuenta({ ...cuenta, codigo: e.target.value })} placeholder="Código" className="rounded-lg border border-gray-300 p-2 text-sm" />
          <input value={cuenta.nombre} onChange={(e) => setCuenta({ ...cuenta, nombre: e.target.value })} placeholder="Nombre" className="rounded-lg border border-gray-300 p-2 text-sm" />
          <select value={cuenta.papel} onChange={(e) => setCuenta({ ...cuenta, papel: e.target.value })} className="rounded-lg border border-gray-300 p-2 text-sm">
            <option value="">Sin papel (la usa el contador)</option>
            {['CAJA', 'BANCOS', 'CLIENTES', 'PROVEEDORES', 'VENTAS', 'COMPRAS', 'IVA_TRASLADADO', 'IVA_ACREDITABLE'].map((p) => <option key={p}>{p}</option>)}
          </select>
          <button type="submit" disabled={!cuenta.codigo.trim() || !cuenta.nombre.trim()}
                  aria-describedby={!cuenta.codigo.trim() || !cuenta.nombre.trim() ? 'motivo-cuenta-contable' : undefined}
                  className="rounded-xl bg-[var(--color-marca)] px-3 py-2 text-sm font-semibold text-white disabled:bg-gray-300">Guardar cuenta</button>
          <Motivo id="motivo-cuenta-contable" cuando={!cuenta.codigo.trim() || !cuenta.nombre.trim()}>Falta el código y el nombre de la cuenta.</Motivo>
        </form>
      </section>
    </div>
  );
}
